import React, { useState, useRef, useEffect } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

interface Props {
   dateFrom: string;
   dateTo: string;
   onChange: (from: string, to: string) => void;
}

const formatDisplay = (dateStr: string) => {
   if (!dateStr) return '';
   const d = new Date(dateStr);
   return d.toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
   });
};

const toDateStr = (date: Date | undefined): string => {
   if (!date) return '';
   const y = date.getFullYear();
   const m = String(date.getMonth() + 1).padStart(2, '0');
   const d = String(date.getDate()).padStart(2, '0');
   return `${y}-${m}-${d}`;
};

const DateRangePicker = ({ dateFrom, dateTo, onChange }: Props) => {
   const [open, setOpen] = useState(false);
   const [draft, setDraft] = useState<DateRange | undefined>(undefined);
   const [alignRight, setAlignRight] = useState(false);
   const [isMobile, setIsMobile] = useState(
      typeof window !== 'undefined' ? window.innerWidth <= 768 : false
   );
   const ref = useRef<HTMLDivElement>(null);

   useEffect(() => {
      const onResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
   }, []);

   useEffect(() => {
      if (!isMobile) return;
      if (open) {
         document.body.style.overflow = 'hidden';
      }
      return () => {
         document.body.style.overflow = '';
      };
   }, [open, isMobile]);

   // Sync draft with props when opening
   useEffect(() => {
      if (open) {
         setDraft(
            dateFrom || dateTo
               ? {
                    from: dateFrom ? new Date(dateFrom + 'T00:00:00') : undefined,
                    to: dateTo ? new Date(dateTo + 'T00:00:00') : undefined,
                 }
               : undefined
         );
      }
   }, [open]);

   // Auto-detect alignment based on position in viewport
   useEffect(() => {
      if (open && ref.current) {
         const rect = ref.current.getBoundingClientRect();
         const spaceRight = window.innerWidth - rect.right;
         setAlignRight(spaceRight < 500);
      }
   }, [open]);

   const handleSelect = (range: DateRange | undefined) => {
      setDraft(range);
   };

   const handleApply = () => {
      onChange(toDateStr(draft?.from), toDateStr(draft?.to));
      setOpen(false);
   };

   const handleCancel = () => {
      setDraft(undefined);
      setOpen(false);
   };

   const applyPreset = (from: Date, to: Date) => {
      onChange(toDateStr(from), toDateStr(to));
      setOpen(false);
   };

   // Close on outside click (desktop only — mobile uses overlay)
   useEffect(() => {
      if (isMobile) return;
      const handler = (e: MouseEvent) => {
         if (ref.current && !ref.current.contains(e.target as Node)) {
            setOpen(false);
         }
      };
      if (open) document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
   }, [open, isMobile]);

   const hasValue = dateFrom || dateTo;
   const displayText =
      dateFrom && dateTo
         ? `${formatDisplay(dateFrom)} — ${formatDisplay(dateTo)}`
         : dateFrom
         ? `From ${formatDisplay(dateFrom)}`
         : dateTo
         ? `Until ${formatDisplay(dateTo)}`
         : 'Select date range';

   const today = new Date();

   const presets: { label: string; from: Date; to: Date }[] = [
      {
         label: 'Today',
         from: today,
         to: today,
      },
      {
         label: 'Last 7 days',
         from: new Date(new Date().setDate(today.getDate() - 7)),
         to: today,
      },
      {
         label: 'Last 30 days',
         from: new Date(new Date().setDate(today.getDate() - 30)),
         to: today,
      },
      {
         label: 'This month',
         from: new Date(today.getFullYear(), today.getMonth(), 1),
         to: today,
      },
      {
         label: 'Last 2 months',
         from: new Date(today.getFullYear(), today.getMonth() - 2, today.getDate()),
         to: today,
      },
      {
         label: 'Last 3 months',
         from: new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()),
         to: today,
      },
      {
         label: 'Last 6 months',
         from: new Date(today.getFullYear(), today.getMonth() - 6, today.getDate()),
         to: today,
      },
      {
         label: 'Last 12 months',
         from: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
         to: today,
      },
      {
         label: 'Last year',
         from: new Date(today.getFullYear() - 1, 0, 1),
         to: new Date(today.getFullYear() - 1, 11, 31),
      },
   ];

   return (
      <Wrapper ref={ref}>
         <Trigger onClick={() => setOpen(!open)} $hasValue={!!hasValue}>
            <CalendarIcon>
               <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
               >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
               </svg>
            </CalendarIcon>
            <TriggerText>{displayText}</TriggerText>
            {hasValue && (
               <ClearX
                  onClick={(e) => {
                     e.stopPropagation();
                     onChange('', '');
                  }}
               >
                  ×
               </ClearX>
            )}
         </Trigger>

         {open && isMobile && (
            <MobileOverlay onClick={() => setOpen(false)} />
         )}
         {open && (
            <Dropdown $alignRight={alignRight} onClick={(e) => e.stopPropagation()}>
               {isMobile && (
                  <SheetHeader>
                     <SheetHandle />
                     <SheetTitleRow>
                        <SheetTitle>Select date range</SheetTitle>
                        <SheetClose onClick={() => setOpen(false)}>×</SheetClose>
                     </SheetTitleRow>
                  </SheetHeader>
               )}
               <Presets>
                  <PresetTitle>Quick select</PresetTitle>
                  <PresetList>
                     {presets.map((preset) => (
                        <PresetBtn
                           key={preset.label}
                           onClick={() => applyPreset(preset.from, preset.to)}
                        >
                           {preset.label}
                        </PresetBtn>
                     ))}
                  </PresetList>
               </Presets>
               <CalendarSection>
                  <CalendarWrapper>
                     <DayPicker
                        mode="range"
                        selected={draft}
                        onSelect={handleSelect}
                        numberOfMonths={isMobile ? 1 : 2}
                        showOutsideDays
                        disabled={{ after: new Date() }}
                     />
                  </CalendarWrapper>
                  <Footer>
                     <FooterInfo>
                        {draft?.from && draft?.to
                           ? `${formatDisplay(toDateStr(draft.from))} — ${formatDisplay(toDateStr(draft.to))}`
                           : draft?.from
                           ? `${formatDisplay(toDateStr(draft.from))} — select end date`
                           : 'Select a date range'}
                     </FooterInfo>
                     <FooterActions>
                        <CancelBtn onClick={handleCancel}>Cancel</CancelBtn>
                        <ApplyBtn onClick={handleApply} disabled={!draft?.from}>
                           Apply
                        </ApplyBtn>
                     </FooterActions>
                  </Footer>
               </CalendarSection>
            </Dropdown>
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div`
   position: relative;
`;

const Trigger = styled.div<{ $hasValue: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   border: 1px solid ${({ $hasValue }) => ($hasValue ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 6px;
   padding: 7px 12px;
   cursor: pointer;
   background: var(--app-surface);
   min-width: 220px;
   transition: border-color 0.2s;

   &:hover {
      border-color: ${colors.PRIMARY};
   }

   @media (max-width: 768px) {
      min-width: 0;
      width: 100%;
   }
`;

const CalendarIcon = styled.span`
   color: ${colors.GRAY4};
   display: flex;
   align-items: center;
`;

const TriggerText = styled.span`
   font-size: 13px;
   color: ${colors.GRAY1};
   flex: 1;
   white-space: nowrap;
`;

const ClearX = styled.span`
   font-size: 18px;
   color: ${colors.GRAY5};
   cursor: pointer;
   line-height: 1;
   padding: 0 2px;

   &:hover {
      color: ${colors.DANGER};
   }
`;

const Dropdown = styled.div<{ $alignRight: boolean }>`
   position: absolute;
   top: calc(100% + 6px);
   ${({ $alignRight }) => ($alignRight ? 'right: 0;' : 'left: 0;')}
   z-index: 1600;
   background: var(--app-surface);
   border-radius: 10px;
   box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
   border: 1px solid ${colors.GRAY13};
   display: flex;
   overflow: hidden;

   @media (max-width: 768px) {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      max-height: 85vh;
      border-radius: 18px 18px 0 0;
      border: none;
      flex-direction: column;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
      animation: slideUp 0.25s cubic-bezier(0.22, 1, 0.36, 1);
   }

   @keyframes slideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
   }
`;

const MobileOverlay = styled.div`
   display: none;
   @media (max-width: 768px) {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.45);
      z-index: 1599;
      animation: fadeIn 0.2s ease;
   }
   @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
   }
`;

const SheetHeader = styled.div`
   display: none;
   @media (max-width: 768px) {
      display: block;
      padding: 6px 16px 4px;
      border-bottom: 1px solid ${colors.GRAY13};
      position: sticky;
      top: 0;
      background: var(--app-surface);
      z-index: 1;
   }
`;

const SheetHandle = styled.div`
   width: 40px;
   height: 4px;
   background: ${colors.GRAY12};
   border-radius: 999px;
   margin: 4px auto 8px;
`;

const SheetTitleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding-bottom: 6px;
`;

const SheetTitle = styled.h3`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK90};
`;

const SheetClose = styled.button`
   background: none;
   border: none;
   font-size: 26px;
   line-height: 1;
   color: ${colors.GRAY4};
   cursor: pointer;
   padding: 0 4px;
`;

const Presets = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   padding: 12px;
   border-right: 1px solid ${colors.GRAY13};
   min-width: 140px;

   @media (max-width: 768px) {
      border-right: none;
      border-bottom: 1px solid ${colors.GRAY13};
      padding: 10px 12px;
      min-width: 0;
   }
`;

const PresetList = styled.div`
   display: contents;
   @media (max-width: 768px) {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 6px;
   }
`;

const PresetTitle = styled.p`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.5px;
   padding: 4px 12px 8px;
`;

const PresetBtn = styled.button`
   background: none;
   border: none;
   text-align: left;
   padding: 7px 12px;
   border-radius: 6px;
   font-size: 13px;
   color: ${colors.GRAY1};
   cursor: pointer;
   white-space: nowrap;

   &:hover {
      background: ${colors.GRAY14};
      color: ${colors.PRIMARY};
   }

   @media (max-width: 768px) {
      background: ${colors.GRAY14};
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 999px;
   }
`;

const CalendarSection = styled.div`
   display: flex;
   flex-direction: column;
`;

const CalendarWrapper = styled.div`
   padding: 12px;

   @media (max-width: 768px) {
      padding: 8px;
      display: flex;
      justify-content: center;
   }

   .rdp-root {
      --rdp-accent-color: ${colors.PRIMARY};
      --rdp-accent-background-color: ${colors.PRIMARY}1a;
      --rdp-range_middle-background-color: ${colors.PRIMARY}1a;
      --rdp-range_start-date-background-color: ${colors.PRIMARY};
      --rdp-range_end-date-background-color: ${colors.PRIMARY};
      --rdp-range_start-background: ${colors.PRIMARY}1a;
      --rdp-range_end-background: ${colors.PRIMARY}1a;
      --rdp-range_middle-color: var(--app-text);
      --rdp-selected-font: 600 13px/1 'Inter', sans-serif;
      --rdp-day_button-border: transparent;
      --rdp-weekday-text-color: var(--app-text-muted);
      font-size: 13px;
      color: var(--app-text);
   }

   .rdp-months {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      gap: 16px;
   }

   .rdp-day {
      border-radius: 6px;
      color: var(--app-text);
   }

   .rdp-caption_label,
   .rdp-month_caption,
   .rdp-nav_button {
      color: var(--app-text);
   }

   .rdp-outside .rdp-day_button,
   .rdp-outside {
      color: var(--app-text-soft);
   }

   .rdp-today:not(.rdp-selected) {
      font-weight: 700;
      color: ${colors.PRIMARY};
   }

   .rdp-disabled {
      color: var(--app-text-soft);
   }
`;

const Footer = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 10px 16px;
   border-top: 1px solid ${colors.GRAY13};
   gap: 8px;
   flex-wrap: wrap;

   @media (max-width: 768px) {
      position: sticky;
      bottom: 0;
      background: var(--app-surface);
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
   }
`;

const FooterInfo = styled.span`
   font-size: 13px;
   color: ${colors.GRAY4};
`;

const FooterActions = styled.div`
   display: flex;
   gap: 8px;
`;

const CancelBtn = styled.button`
   background: none;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   padding: 6px 16px;
   font-size: 13px;
   color: ${colors.GRAY1};
   cursor: pointer;

   &:hover {
      background: ${colors.GRAY14};
   }
`;

const ApplyBtn = styled.button<{ disabled?: boolean }>`
   background: ${({ disabled }) => (disabled ? colors.GRAY12 : colors.PRIMARY)};
   border: none;
   border-radius: 6px;
   padding: 6px 16px;
   font-size: 13px;
   color: ${({ disabled }) => (disabled ? colors.GRAY5 : 'white')};
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
   font-weight: 500;

   &:hover:not(:disabled) {
      opacity: 0.9;
   }
`;

export default DateRangePicker;
